import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, FileCheck2, Link2, Loader2, MessageSquareText } from "lucide-react";
import { smsHref, firstName } from "@/lib/sms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDay = (d) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";

/** Accepts either an On-Site item (checkin_submitted/checkin_docs) or a raw
 * booking doc (checkin_form) and returns a single normalised shape. */
const normalize = (item) => {
  const form = item.checkin_form || null;
  const submitted = item.checkin_submitted ?? !!form;
  const rawFishing = (form?.fishing || []).filter((f) => f && typeof f === "object");
  const rawFishingDue = Math.max(
    0,
    rawFishing.reduce((n, f) => n + (Number(f.price) || 0), 0) - (Number(item.fishing_paid_amount) || 0)
  );
  const docs = item.checkin_docs || {
    id_document_url: form?.id_document_asset ? `/api/assets/${form.id_document_asset}` : "",
    id_type: form?.id_type || "",
    submitted_at: form?.submitted_at || null,
    fishing: rawFishing,
    fishing_due: Math.round(rawFishingDue * 100) / 100,
    party: (form?.party || [])
      .filter((p) => p && typeof p === "object")
      .map((p) => ({
        name: p.name || "",
        fishing: !!p.fishing,
        license_url: p.license_asset ? `/api/assets/${p.license_asset}` : "",
      })),
  };
  return { submitted, docs };
};

/**
 * Online check-in cell (shared by On-Site + Arrivals): send the hotel-style
 * form link — email goes out automatically when we hold an address, plus Copy
 * and Text buttons for walk-ins — or show what's come back (party, docs,
 * Print/PDF record).
 */
const CheckInLinkCell = ({ item }) => {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const { submitted, docs } = normalize(item);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${item.id}/checkin-link`);
      setLink(data);
      toast.success(data.emailed ? "Check-in link emailed to the guest." : "Link ready — copy it or text it over (no email on file).");
    } catch {
      toast.error("Couldn't create the check-in link.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Link copied — paste it into a text or WhatsApp.");
    } catch {
      toast.error("Couldn't copy — long-press the link instead.");
    }
  };

  const textHref = link && item.guest_phone
    ? smsHref(item.guest_phone, `Hi ${firstName(item.guest_name)}, you can check in for your Walnut Lakes stay online here (takes a minute): ${link.url}`)
    : "";

  if (submitted) {
    return (
      <div className="space-y-1.5" data-testid={`checkin-done-${item.id}`}>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold bg-[#3B8C4E]/15 text-[#3B8C4E]">
          <FileCheck2 size={10} /> Check-in form received
        </span>
        {item.emergency_phone && (
          <p className="text-[11px] text-[#6A5F58]">Emergency: <span className="font-medium text-[#14100F]">{item.emergency_phone}</span></p>
        )}
        {(docs.fishing || []).length > 0 && (
          <p className="text-[11px] text-[#6A5F58]" data-testid={`checkin-fishing-${item.id}`}>
            Fishing: {docs.fishing.map((f) => `${fmtDay(f.date)} · ${f.rods} rod${f.rods > 1 ? "s" : ""}`).join(", ")}
            {" — "}
            {docs.fishing.some((f) => Number(f.price) > 0)
              ? (docs.fishing_due > 0 ? <span className="font-semibold text-[#B3394F]">£{docs.fishing_due.toFixed(2)} to collect</span> : <span className="font-semibold text-[#3B8C4E]">passes paid</span>)
              : <span className="text-[#3B8C4E]">included with lodge</span>}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 text-[10px] font-brand tracking-[0.1em] uppercase">
          {(docs.party || [])
            .filter((p) => p.fishing && p.license_url)
            .map((p, i) => (
              <a
                key={i}
                href={`${BACKEND}${p.license_url}`}
                target="_blank"
                rel="noreferrer"
                className="px-2.5 py-1 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6]"
                data-testid={`checkin-doc-license-${item.id}-${i}`}
              >
                Licence · {(p.name || `Guest ${i + 1}`).split(" ")[0]}
              </a>
            ))}
          {docs.id_document_url && (
            <a href={`${BACKEND}${docs.id_document_url}`} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6]" data-testid={`checkin-doc-id-${item.id}`}>
              ID{docs.id_type === "passport" ? " (passport)" : docs.id_type === "driving_licence" ? " (driving licence)" : ""}
            </a>
          )}
          <a href={`/admin/checkin/${item.id}`} target="_blank" rel="noreferrer" className="px-2.5 py-1 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79]" data-testid={`checkin-doc-print-${item.id}`}>
            Print / PDF
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={send}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white text-[10px] font-brand tracking-[0.12em] uppercase transition disabled:opacity-50"
        data-testid={`checkin-send-${item.id}`}
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />} Send check-in link
      </button>
      {link && (
        <div className="flex items-center gap-1.5" data-testid={`checkin-link-${item.id}`}>
          <input readOnly value={link.url} onFocus={(e) => e.target.select()} className="h-8 flex-1 min-w-0 rounded-md border border-[#DDD1BE] bg-[#F5EFE6] px-2 text-[11px] font-mono" />
          <button type="button" onClick={copy} className="p-1.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#3B8C4E]" title="Copy link" data-testid={`checkin-copy-${item.id}`}>
            <Copy size={12} />
          </button>
          {textHref && (
            <a href={textHref} className="p-1.5 rounded-full bg-[#3B8C4E] text-white hover:bg-[#0F0E0E]" title="Text the link to the guest" data-testid={`checkin-text-${item.id}`}>
              <MessageSquareText size={12} />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckInLinkCell;
